import { Button, Card, Modal, PanoramaViewer, detectPanorama } from '@citizen-walk/shared-ui';
import {
  getModeTemplate,
  type BulkPhotoDestination,
  type BulkPhotoPostItem,
  type DataSettings,
  type DistrictMode,
  type FieldDef,
  type PostPhotoMeta,
  type PostVisibility,
  formatMembershipProfileValue,
} from '@citizen-walk/shared-types';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { shortUid } from '../../lib/uid';
import { mergeFields } from '../data-settings';
import { useDistrict } from '../districts-admin';
import { useSessions } from '../sessions-admin';
import { readPhotoExif } from './exifLocation';
import { LocationPicker } from './LocationPicker';
import { PhotoPostFieldsForm } from './PhotoPostFieldsForm';
import { PhotosMap } from './PhotosMap';
import { generatePreviewThumbnail } from './thumbnail';
import { useBulkCreatePhotoPosts, useListParticipants } from './useBulkPhotoImport';
import { uploadBulkPhotos, type BulkPhotoUploadResult } from './bulkPhotoUpload';

/**
 * 写真まとめて一括投稿ツール (issue #23、Phase B〜D = アップロード + 確認 + 個別入力 + 投稿作成)。
 *
 * 流れ: 投稿者(参加者)選択 + セッション → 端末別アップロード(複数/フォルダ/カメラ) → EXIF 位置/撮影時刻
 * 読取 + サムネ生成 → 地図プロット + カード確認 → 各写真の個別入力(タイトル/コメント/位置の手動ピン/
 * 付帯項目/公開範囲、前後遷移 + ←→ショートカット、**直前位置の持ち回り**) → 検証(ドライラン) → 投稿作成
 * (Storage へアップロード → bulkCreatePhotoPosts、進捗表示)。写真処理は地区 photoConfig に従い、
 * 参加者投稿と完全に同一 (issue #28)。
 *
 * 他者名義の作成は Admin SDK callable (bulkCreatePhotoPosts) 経由のみ (firestore.rules は本人限定のまま)。
 */

const VISIBILITY_LABELS: Record<PostVisibility, string> = {
  public: '公開（地図で全員に表示）',
  hidden_others: '本人のみ（地図で他の人に非表示）',
  hidden_all: '非公開（管理者のみ）',
};

/** 360度写真の登録先 (docs/27 §5-1)。平面写真は常に通常投稿。 */
const DESTINATION_LABELS: Record<BulkPhotoDestination, string> = {
  post: '通常の投稿として登録',
  streetview: 'ストリートビュー机上調査のデータとして登録',
  both: '両方に登録',
};

function isPanoramaItem(it: { photo: PostPhotoMeta | null }): boolean {
  return it.photo?.projection === 'equirectangular';
}

interface LatLng {
  latitude: number;
  longitude: number;
}

interface PhotoItem {
  id: string;
  file: File;
  fileName: string;
  /** プレビュー用サムネ objectURL (HEIC 等 decode 不可は null)。 */
  thumbUrl: string | null;
  /** 位置 (EXIF 由来 or 手動ピン、無ければ null)。 */
  location: LatLng | null;
  /** 位置の取得元 ('gps'=EXIF / 'manual'=手動ピン)。 */
  locationSource: 'gps' | 'manual' | null;
  /** 撮影時刻 (ms epoch、並び替え/重複判定用)。 */
  captureTimeMs: number | null;
  title: string;
  comment: string;
  /** 地区 postFields 駆動の付帯項目。 */
  extraFields: Record<string, unknown>;
  visibility: PostVisibility;
  /** 写真の幾何メタ (docs/27、元ファイルから判定。360度写真なら projection='equirectangular')。 */
  photo: PostPhotoMeta | null;
  /** 登録先 (360度写真のみ選択可、平面写真は常に 'post')。 */
  destination: BulkPhotoDestination;
}

type CommitPhase = 'idle' | 'confirm' | 'uploading' | 'creating' | 'done';

interface CommitResult {
  created: number;
  /** 机上調査データとして登録した件数 (docs/27)。 */
  streetViewCreated: number;
  skipped: number;
  /** EXIF 除去アップロードに失敗した写真 (HEIC 等)。 */
  uploadFailed: { fileName: string; message: string }[];
  /** callable が返した item 単位のエラー。 */
  serverErrors: { index: number; message: string }[];
  /** 実際に作成へ送った (アップロード成功した) item の id。完了後にこの id でリストから除去する。 */
  committedIds: string[];
}

function newLocalId(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) return crypto.randomUUID();
  return `pi-${Date.now()}-${Math.floor(Math.random() * 1e9)}`;
}

function isImageFile(f: File): boolean {
  return f.type.startsWith('image/') || /\.(jpe?g|png|heic|heif|webp)$/i.test(f.name);
}

/**
 * item が必須項目 key の値を満たすか。組込キー (photo/title/comment/detail/location) は専用判定、
 * それ以外 (KeikanType 等の付帯項目) は extraFields[key] の有無で判定。
 * - photo: 一括投稿は写真前提のため常に満たす。
 * - comment / detail: 一括投稿の自由記述 (it.comment) で満たす。
 */
function hasFieldValue(it: PhotoItem, key: string): boolean {
  if (key === 'photo') return true;
  if (key === 'title') return it.title.trim() !== '';
  if (key === 'comment' || key === 'detail') return it.comment.trim() !== '';
  if (key === 'location') return it.location !== null;
  const v = it.extraFields[key];
  if (v == null) return false;
  if (typeof v === 'string') return v.trim() !== '';
  if (Array.isArray(v)) return v.length > 0;
  return true; // number / boolean / date 等は存在すれば満たす
}

/**
 * 投稿可能か。入力必須は地区の入力項目設定 (postFields の mode='required') に統一する
 * (旧来の「タイトルかコメント + 位置」固定チェックは廃止)。
 */
function isItemReady(it: PhotoItem, requiredKeys: readonly string[]): boolean {
  // 机上調査データのみ: 参加者の必須項目は不要、位置だけ必須 (docs/27 §5-1)。
  if (it.destination === 'streetview') return it.location !== null;
  const base = requiredKeys.every((k) => hasFieldValue(it, k));
  if (it.destination === 'both') return base && it.location !== null;
  return base;
}

/** 写真の判定結果 + 一括登録固有のメタ (ファイル名・撮影時刻) を投稿 doc 用の形にする。 */
function toPhotoMeta(it: PhotoItem): PostPhotoMeta | null {
  if (!it.photo) return null;
  return {
    ...it.photo,
    fileName: it.fileName,
    capturedAt: it.captureTimeMs ? new Date(it.captureTimeMs).toISOString() : null,
  };
}

export function BulkPhotoImportPage() {
  const { districtId = '' } = useParams<{ districtId: string }>();
  const { state: pState, load } = useListParticipants();
  const sessionsState = useSessions(districtId);
  const districtState = useDistrict(districtId || null);
  const { call: callBulkCreate } = useBulkCreatePhotoPosts();

  const [authorUid, setAuthorUid] = useState('');
  const [sessionId, setSessionId] = useState('');
  const [items, setItems] = useState<PhotoItem[]>([]);
  // 360度写真の既定の登録先 (docs/27 §5-1)。変更すると取込済みの 360度写真にも一括適用する。
  const [defaultDestination, setDefaultDestination] = useState<BulkPhotoDestination>('post');
  const [processing, setProcessing] = useState(false);
  const [detailIndex, setDetailIndex] = useState<number | null>(null);

  const [commitPhase, setCommitPhase] = useState<CommitPhase>('idle');
  const [uploadProgress, setUploadProgress] = useState<{ done: number; total: number }>({
    done: 0,
    total: 0,
  });
  const [dryRunInfo, setDryRunInfo] = useState<{ valid: number; errors: number } | null>(null);
  const [dryRunning, setDryRunning] = useState(false);
  const [commitResult, setCommitResult] = useState<CommitResult | null>(null);

  const filesInputRef = useRef<HTMLInputElement>(null);
  const folderInputRef = useRef<HTMLInputElement>(null);
  const cameraInputRef = useRef<HTMLInputElement>(null);

  // 直前に手動入力した位置 (連続撮影の調査で次の写真の地図初期表示に持ち回る)。
  const [lastLocation, setLastLocation] = useState<LatLng | null>(null);

  // 現在 items を保持し、unmount 時に objectURL を確実に revoke する (leak 防止)。
  const itemsRef = useRef<PhotoItem[]>([]);
  useEffect(() => {
    itemsRef.current = items;
  }, [items]);

  useEffect(() => {
    if (districtId) void load(districtId);
  }, [districtId, load]);

  // folder 選択用 input に webkitdirectory を付与 (React 標準 prop に無いため ref で設定)。PC のみ有効。
  useEffect(() => {
    const el = folderInputRef.current;
    if (el) {
      el.setAttribute('webkitdirectory', '');
      el.setAttribute('directory', '');
    }
  }, []);

  useEffect(
    () => () => {
      for (const it of itemsRef.current) if (it.thumbUrl) URL.revokeObjectURL(it.thumbUrl);
    },
    [],
  );

  const district = districtState.status === 'ready' ? districtState.district : null;
  const mode: DistrictMode = (district?.mode as DistrictMode | undefined) ?? 'bousai';
  // 写真の前処理 (解像度/サイズ/品質/無圧縮) は参加者投稿と同じく地区 photoConfig に従う (issue #28)。
  const photoConfig = district?.photoConfig;

  // 地区 mode テンプレ default + 保存値を merge した postFields (個別入力フォームの駆動元)。
  const postFields: FieldDef[] = useMemo(() => {
    const tpl = getModeTemplate(mode);
    const saved = (district?.dataSettings ?? {}) as Partial<DataSettings>;
    return mergeFields(tpl.dataSettings.postFields, saved.postFields ?? [], 'postFields');
  }, [district, mode]);

  // 入力必須は地区の入力項目設定 (mode='required') に統一する (issue #23 follow-up)。
  const requiredKeys = useMemo(
    () => postFields.filter((f) => f.mode === 'required').map((f) => f.key),
    [postFields],
  );

  const author = useMemo(
    () => pState.participants.find((p) => p.uid === authorUid) ?? null,
    [pState.participants, authorUid],
  );
  const authorSessions = useMemo(() => author?.joinedSessionIds ?? [], [author]);

  const sessionName = useCallback(
    (id: string): string => {
      const items_ = sessionsState.status === 'ready' ? sessionsState.items : [];
      return items_.find((s) => s.id === id)?.name ?? id;
    },
    [sessionsState],
  );

  // 投稿者を選び直したら、参加セッションが 1 つなら自動選択、それ以外は未選択に。
  useEffect(() => {
    // 投稿者選択 (外部入力) に同期した派生 reset。codebase 既定の suppress に倣う。
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setSessionId(authorSessions.length === 1 ? (authorSessions[0] ?? '') : '');
  }, [authorUid, authorSessions]);

  // handleFiles は useCallback([]) のため、既定の登録先は ref 経由で最新値を読む。
  const defaultDestinationRef = useRef<BulkPhotoDestination>('post');
  useEffect(() => {
    defaultDestinationRef.current = defaultDestination;
  }, [defaultDestination]);

  const handleFiles = useCallback(async (fileList: FileList | null) => {
    if (!fileList || fileList.length === 0) return;
    const files = Array.from(fileList).filter(isImageFile);
    if (files.length === 0) return;
    setProcessing(true);
    try {
      const next: PhotoItem[] = [];
      for (const file of files) {
        // EXIF / 360度判定は加工前の原本から読む (前処理で XMP が消える、docs/27 §3-1)。
        // サムネは別途生成 (HEIC は null になりうる)。
        const [exif, thumbBlob, detection] = await Promise.all([
          readPhotoExif(file),
          generatePreviewThumbnail(file),
          detectPanorama(file),
        ]);
        const photo: PostPhotoMeta = {
          projection: detection.projection,
          width: detection.width,
          height: detection.height,
          poseHeadingDeg: detection.poseHeadingDeg,
          detectedBy: detection.detectedBy,
        };
        next.push({
          id: newLocalId(),
          file,
          fileName: file.name,
          thumbUrl: thumbBlob ? URL.createObjectURL(thumbBlob) : null,
          location: exif.location,
          locationSource: exif.location ? 'gps' : null,
          captureTimeMs: exif.captureTimeMs,
          title: '',
          comment: '',
          extraFields: {},
          visibility: 'public',
          photo,
          destination:
            photo.projection === 'equirectangular' ? defaultDestinationRef.current : 'post',
        });
      }
      setItems((prev) =>
        [...prev, ...next].sort(
          (a, b) => (a.captureTimeMs ?? Infinity) - (b.captureTimeMs ?? Infinity),
        ),
      );
    } finally {
      setProcessing(false);
    }
  }, []);

  function removeItem(id: string) {
    setItems((prev) => {
      const t = prev.find((it) => it.id === id);
      if (t?.thumbUrl) URL.revokeObjectURL(t.thumbUrl);
      return prev.filter((it) => it.id !== id);
    });
  }

  const patchItem = useCallback((id: string, partial: Partial<PhotoItem>) => {
    setItems((prev) => prev.map((it) => (it.id === id ? { ...it, ...partial } : it)));
  }, []);

  const setItemLocation = useCallback(
    (id: string, loc: LatLng) => {
      setLastLocation(loc);
      patchItem(id, { location: loc, locationSource: 'manual' });
    },
    [patchItem],
  );

  const updateExtraField = useCallback((id: string, key: string, value: unknown) => {
    setItems((prev) =>
      prev.map((it) =>
        it.id === id ? { ...it, extraFields: { ...it.extraFields, [key]: value } } : it,
      ),
    );
  }, []);

  const stats = useMemo(() => {
    let ready = 0;
    let noLoc = 0;
    let noText = 0;
    let readyPost = 0;
    let readyStreetView = 0;
    for (const it of items) {
      if (isItemReady(it, requiredKeys)) {
        ready++;
        if (it.destination !== 'streetview') readyPost++;
        if (it.destination !== 'post') readyStreetView++;
      } else if (it.location === null) noLoc++;
      else noText++;
    }
    return { ready, noLoc, noText, readyPost, readyStreetView };
  }, [items, requiredKeys]);

  // docs/27 §5: 360度写真の有無と、机上調査の実施可否 (地区の analysisEnabled)。
  const panoramaCount = useMemo(() => items.filter(isPanoramaItem).length, [items]);
  const streetViewAvailable = district?.streetView?.analysisEnabled === true;
  // 通常投稿を 1 件でも作るなら投稿者が必要。全件が机上調査データなら不要。
  const needsAuthor = useMemo(
    () => items.length === 0 || items.some((it) => it.destination !== 'streetview'),
    [items],
  );

  /** 既定の登録先を変更し、取込済みの 360度写真にも一括適用する。 */
  function applyDefaultDestination(dest: BulkPhotoDestination) {
    setDefaultDestination(dest);
    setItems((prev) => prev.map((it) => (isPanoramaItem(it) ? { ...it, destination: dest } : it)));
  }

  const withLocation = items.filter((it) => it.location !== null).length;
  const lowLocation = items.length > 0 && withLocation < items.length;

  const mapPoints = useMemo(
    () =>
      items
        .filter((it) => it.location !== null)
        .map((it) => ({
          id: it.id,
          latitude: it.location!.latitude,
          longitude: it.location!.longitude,
          selected: detailIndex !== null && items[detailIndex]?.id === it.id,
          panorama: isPanoramaItem(it),
        })),
    [items, detailIndex],
  );

  // 個別入力の地図初期表示中心: 直前手動位置 → 最初の位置あり写真 → (なければ Japan)。
  const fallbackCenter = useMemo<LatLng | undefined>(() => {
    if (lastLocation) return lastLocation;
    const located = items.find((it) => it.location !== null);
    return located?.location ?? undefined;
  }, [items, lastLocation]);

  // detail modal: ←/→ で前後の写真へ。入力中 (input/textarea/select) はキー移動を無効化。
  useEffect(() => {
    if (detailIndex === null) return;
    function onKey(e: KeyboardEvent) {
      const tag = (e.target as HTMLElement | null)?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        setDetailIndex((i) => (i === null ? i : Math.max(0, i - 1)));
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        setDetailIndex((i) => (i === null ? i : Math.min(itemsRef.current.length - 1, i + 1)));
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [detailIndex]);

  // 写真が削除され index が範囲外になっても items[detailIndex] が undefined → 下の detailItem が
  // null になり modal が自動で閉じる (clamp 用の effect は不要)。
  const detailItem = detailIndex !== null ? (items[detailIndex] ?? null) : null;
  // 360度写真の個別入力プレビュー用に原本の objectURL を作る (見回し表示、閉じたら revoke)。
  const detailPanoramaUrl = useMemo(
    () => (detailItem && isPanoramaItem(detailItem) ? URL.createObjectURL(detailItem.file) : null),
    [detailItem],
  );
  useEffect(() => {
    return () => {
      if (detailPanoramaUrl) URL.revokeObjectURL(detailPanoramaUrl);
    };
  }, [detailPanoramaUrl]);

  // ---- 投稿作成 (Phase D) ----

  const readyItems = useMemo(
    () => items.filter((it) => isItemReady(it, requiredKeys)),
    [items, requiredKeys],
  );

  /** ドライラン: アップロードせず、サーバ側 validation だけで件数を確認する。 */
  async function runDryRun() {
    if ((needsAuthor && !author) || readyItems.length === 0) return;
    setDryRunning(true);
    setDryRunInfo(null);
    try {
      const payload: BulkPhotoPostItem[] = readyItems.map((it) => ({
        title: it.title.trim(),
        comment: it.comment.trim() || null,
        location: it.location
          ? { latitude: it.location.latitude, longitude: it.location.longitude }
          : null,
        source: it.location ? (it.locationSource === 'gps' ? 'gps' : 'manual') : null,
        extraFields: it.extraFields,
        captureTimeMs: it.captureTimeMs,
        visibility: it.visibility,
        photo: toPhotoMeta(it),
        destination: it.destination,
      }));
      const res = await callBulkCreate({
        districtId,
        authorUid: author?.uid ?? null,
        authorNickname: author?.nickname ?? null,
        sessionId: sessionId || null,
        dryRun: true,
        items: payload,
      });
      if (res) {
        setDryRunInfo({ valid: res.totalItems - res.errors.length, errors: res.errors.length });
      }
    } finally {
      setDryRunning(false);
    }
  }

  /** 本実行: EXIF 除去アップロード → bulkCreatePhotoPosts。 */
  async function runCommit() {
    if ((needsAuthor && !author) || readyItems.length === 0) return;
    setCommitPhase('uploading');
    setUploadProgress({ done: 0, total: readyItems.length });
    setCommitResult(null);

    // 1) Storage へアップロード (地区 photoConfig 駆動の前処理。HEIC 等は失敗 → 当該写真は除外)。
    //    360度写真は 4096px まで保持する (docs/27 §3-2)。
    const { ok, errors: uploadErrors } = await uploadBulkPhotos(
      districtId,
      readyItems.map((it) => ({ id: it.id, file: it.file, equirectangular: isPanoramaItem(it) })),
      photoConfig,
      (done, total) => setUploadProgress({ done, total }),
    );
    const uploadFailed = uploadErrors.map((e) => ({
      fileName: readyItems.find((it) => it.id === e.id)?.fileName ?? e.id,
      message: e.message,
    }));

    // 2) アップロードできた写真だけで投稿 item を組み立てる。
    const uploadable = readyItems.filter((it) => ok.has(it.id));
    if (uploadable.length === 0) {
      setCommitResult({
        created: 0,
        streetViewCreated: 0,
        skipped: 0,
        uploadFailed,
        serverErrors: [],
        committedIds: [],
      });
      setCommitPhase('done');
      return;
    }

    setCommitPhase('creating');
    const payload: BulkPhotoPostItem[] = uploadable.map((it) => {
      const up = ok.get(it.id) as BulkPhotoUploadResult;
      return {
        title: it.title.trim(),
        comment: it.comment.trim() || null,
        location: it.location
          ? { latitude: it.location.latitude, longitude: it.location.longitude }
          : null,
        source: it.location ? (it.locationSource === 'gps' ? 'gps' : 'manual') : null,
        photoPath: up.photoPath,
        photoThumbPath: up.photoThumbPath,
        photoUrl: up.photoUrl,
        photoThumbUrl: up.photoThumbUrl,
        extraFields: it.extraFields,
        captureTimeMs: it.captureTimeMs,
        visibility: it.visibility,
        photo: toPhotoMeta(it),
        destination: it.destination,
      };
    });

    // 3) callable で投稿一括作成。
    const res = await callBulkCreate({
      districtId,
      authorUid: author?.uid ?? null,
      authorNickname: author?.nickname ?? null,
      sessionId: sessionId || null,
      dryRun: false,
      items: payload,
    });

    setCommitResult({
      created: res?.created ?? 0,
      streetViewCreated: res?.streetViewCreated ?? 0,
      skipped: res?.skipped ?? 0,
      uploadFailed,
      serverErrors: res?.errors ?? (res ? [] : [{ index: -1, message: '投稿作成に失敗しました' }]),
      committedIds: uploadable.map((it) => it.id),
    });
    setCommitPhase('done');
  }

  /** 投稿作成成功後、作成できた写真を id でリストから取り除く (重複作成防止)。同名ファイル混在でも安全。 */
  function clearCommitted() {
    if (commitResult && commitResult.created + commitResult.streetViewCreated > 0) {
      const removeIds = new Set(commitResult.committedIds);
      setItems((prev) => {
        for (const it of prev) {
          if (removeIds.has(it.id) && it.thumbUrl) URL.revokeObjectURL(it.thumbUrl);
        }
        return prev.filter((it) => !removeIds.has(it.id));
      });
    }
    setCommitPhase('idle');
    setCommitResult(null);
    setDryRunInfo(null);
  }

  const canCommit = (!needsAuthor || !!author) && stats.ready > 0;

  return (
    <div>
      <h1 className="text-2xl font-bold text-slate-900">写真まとめて一括投稿</h1>
      <p className="text-sm text-slate-500 mt-1">
        複数の写真をまとめて取り込み、参加者の名義で投稿を一括作成します。写真に位置情報があれば自動で地図に反映します。
      </p>

      {/* 1. 投稿者・セッション */}
      <Card padding="md" className="mt-4">
        <h2 className="text-sm font-bold text-slate-700 mb-2">1. 投稿者とセッション</h2>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <label className="block text-sm">
            <span className="text-slate-600">投稿者（参加者）</span>
            {pState.status === 'loading' && (
              <span className="ml-2 text-xs text-slate-400">読み込み中…</span>
            )}
            {pState.status === 'error' && (
              <span className="ml-2 text-xs text-danger-600">取得に失敗しました</span>
            )}
            <select
              value={authorUid}
              onChange={(e) => setAuthorUid(e.target.value)}
              className="mt-1 block w-full rounded border border-slate-300 px-2 py-1.5 text-sm"
            >
              <option value="">— 参加者を選択 —</option>
              {pState.participants.map((p) => (
                <option key={p.uid} value={p.uid}>
                  {p.nickname || '(ニックネーム未設定)'} ・ID {shortUid(p.uid)}
                </option>
              ))}
            </select>
          </label>

          <label className="block text-sm">
            <span className="text-slate-600">セッション</span>
            <select
              value={sessionId}
              onChange={(e) => setSessionId(e.target.value)}
              disabled={!author}
              className="mt-1 block w-full rounded border border-slate-300 px-2 py-1.5 text-sm disabled:bg-slate-100"
            >
              <option value="">{author ? '— セッションなし —' : '先に投稿者を選択'}</option>
              {authorSessions.map((sid) => (
                <option key={sid} value={sid}>
                  {sessionName(sid)}
                </option>
              ))}
            </select>
            {author && authorSessions.length === 0 && (
              <span className="mt-1 block text-xs text-slate-400">
                この参加者は参加セッションがありません（セッションなしで作成されます）。
              </span>
            )}
          </label>
        </div>
        {author && (
          <p className="mt-2 text-xs text-slate-500">
            投稿者: <span className="font-medium text-slate-700">{author.nickname}</span>
            {formatMembershipProfileValue(author.ageGroup)
              ? ` / ${formatMembershipProfileValue(author.ageGroup)}`
              : ''}
            {formatMembershipProfileValue(author.gender)
              ? ` / ${formatMembershipProfileValue(author.gender)}`
              : ''}
            （投稿はこの参加者の名義で作成され、属性・セッションは集計時に紐づきます）
          </p>
        )}
        {!needsAuthor && (
          <p className="mt-2 text-xs text-slate-500">
            取り込んだ写真はすべて机上調査データとして登録するため、投稿者の選択は不要です。
          </p>
        )}

        {/* docs/27 §5-1: 360度写真の登録先 (360度写真が 1 枚以上あるときだけ表示)。 */}
        {panoramaCount > 0 && (
          <div className="mt-3 rounded border border-primary-200 bg-primary-50 px-3 py-2">
            <p className="text-sm font-medium text-slate-700">
              360度写真の登録先（{panoramaCount} 枚）
            </p>
            <div className="mt-1 grid grid-cols-1 gap-1 sm:grid-cols-3">
              {(Object.keys(DESTINATION_LABELS) as BulkPhotoDestination[]).map((dest) => {
                const disabled = dest !== 'post' && !streetViewAvailable;
                return (
                  <label
                    key={dest}
                    className={`flex items-start gap-2 text-xs ${disabled ? 'text-slate-400' : 'text-slate-700'}`}
                  >
                    <input
                      type="radio"
                      name="panorama-destination"
                      value={dest}
                      checked={defaultDestination === dest}
                      disabled={disabled}
                      onChange={() => applyDefaultDestination(dest)}
                      className="mt-0.5"
                    />
                    <span>{DESTINATION_LABELS[dest]}</span>
                  </label>
                );
              })}
            </div>
            <p className="mt-1 text-[11px] text-slate-500">
              {streetViewAvailable
                ? '机上調査データは非公開の地点として登録され、「ストリートビュー机上調査」ページで解析対象を選んで AI 解析します。写真ごとの登録先は個別入力で変更できます。'
                : 'この地区ではストリートビュー机上調査が有効化されていないため、机上調査データとしては登録できません（マスター管理者が地区の AI 設定で有効化できます）。'}
            </p>
          </div>
        )}
      </Card>

      {/* 2. アップロード */}
      <Card padding="md" className="mt-4">
        <h2 className="text-sm font-bold text-slate-700 mb-2">2. 写真を取り込む</h2>
        <div className="flex flex-wrap gap-2">
          <Button variant="primary" size="sm" onClick={() => filesInputRef.current?.click()}>
            📷 写真を選ぶ（複数可）
          </Button>
          <Button variant="secondary" size="sm" onClick={() => folderInputRef.current?.click()}>
            📁 フォルダを選ぶ（PC）
          </Button>
          <Button variant="secondary" size="sm" onClick={() => cameraInputRef.current?.click()}>
            📸 カメラで撮る
          </Button>
          {processing && <span className="self-center text-xs text-slate-500">読み込み中…</span>}
        </div>
        <input
          ref={filesInputRef}
          type="file"
          accept="image/*"
          multiple
          className="hidden"
          onChange={(e) => {
            void handleFiles(e.target.files);
            e.target.value = '';
          }}
        />
        <input
          ref={folderInputRef}
          type="file"
          multiple
          className="hidden"
          onChange={(e) => {
            void handleFiles(e.target.files);
            e.target.value = '';
          }}
        />
        <input
          ref={cameraInputRef}
          type="file"
          accept="image/*"
          capture="environment"
          className="hidden"
          onChange={(e) => {
            void handleFiles(e.target.files);
            e.target.value = '';
          }}
        />
        {items.length > 0 && (
          <p className="mt-2 text-xs text-slate-600">
            {items.length} 枚中 <span className="font-semibold">{withLocation} 枚</span>{' '}
            に位置情報があります。
          </p>
        )}
        {lowLocation && (
          <div className="mt-2 rounded bg-warning-50 border border-warning-200 px-3 py-2 text-xs text-warning-800">
            位置情報が読めない写真があります。iPhone から直接アップロードすると iOS
            が位置情報を削除する場合があります。
            <strong>AirDrop / USB / iCloud の「オリジナルをダウンロード」</strong>
            で原本を PC に取り込んでからアップロードすると位置情報が残ります。
            （位置のない写真は、各写真の「個別入力」で地図から手動で指定できます）
          </div>
        )}
      </Card>

      {/* 3. 地図プロット */}
      {items.length > 0 && (
        <Card padding="md" className="mt-4">
          <h2 className="text-sm font-bold text-slate-700 mb-2">3. 地図で確認</h2>
          {mapPoints.length === 0 ? (
            <p className="text-sm text-slate-400">
              位置情報のある写真がまだありません。各写真の「個別入力」で手動指定できます。
            </p>
          ) : (
            <>
              <PhotosMap
                points={mapPoints}
                onSelectPoint={(id) => {
                  const idx = items.findIndex((it) => it.id === id);
                  if (idx >= 0) setDetailIndex(idx);
                }}
              />
              <p className="mt-2 text-xs text-slate-400">
                ピンをクリックするとその写真の個別入力を開きます。
              </p>
            </>
          )}
        </Card>
      )}

      {/* 4. 確認 */}
      <Card padding="md" className="mt-4">
        <div className="flex items-center justify-between">
          <h2 className="text-sm font-bold text-slate-700">4. 内容を確認（{items.length} 枚）</h2>
        </div>
        {items.length === 0 ? (
          <p className="mt-2 text-sm text-slate-400">
            まだ写真がありません。上の「写真を選ぶ」から取り込んでください。
          </p>
        ) : (
          <ul className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {items.map((it, idx) => (
              <li
                key={it.id}
                className="rounded border border-slate-200 overflow-hidden bg-white flex flex-col"
              >
                <div className="relative aspect-video bg-slate-100 flex items-center justify-center">
                  {it.thumbUrl ? (
                    <img src={it.thumbUrl} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <span className="text-xs text-slate-400">プレビュー不可（HEIC 等）</span>
                  )}
                  <span className="absolute top-1 left-1 flex flex-wrap gap-1">
                    <span
                      className={`text-[10px] px-1.5 py-0.5 rounded ${
                        it.location ? 'bg-primary-600 text-white' : 'bg-warning-500 text-white'
                      }`}
                    >
                      {it.location
                        ? it.locationSource === 'manual'
                          ? '📍 手動'
                          : '📍 位置あり'
                        : '位置なし'}
                    </span>
                    {isPanoramaItem(it) && (
                      <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-900/70 text-white">
                        360°
                        {it.destination === 'streetview'
                          ? ' 机上調査'
                          : it.destination === 'both'
                            ? ' 投稿+机上調査'
                            : ''}
                      </span>
                    )}
                  </span>
                  <button
                    type="button"
                    onClick={() => removeItem(it.id)}
                    className="absolute top-1 right-1 text-[10px] px-1.5 py-0.5 rounded bg-slate-900/60 text-white hover:bg-slate-900/80"
                  >
                    削除
                  </button>
                </div>
                <div className="p-2 flex flex-col gap-1">
                  <input
                    type="text"
                    value={it.title}
                    onChange={(e) => patchItem(it.id, { title: e.target.value })}
                    placeholder="タイトル"
                    className="block w-full rounded border border-slate-300 px-2 py-1 text-sm"
                  />
                  <p className="text-[10px] text-slate-400 truncate" title={it.fileName}>
                    {it.fileName}
                    {it.captureTimeMs
                      ? ` ・ ${new Date(it.captureTimeMs).toLocaleString('ja-JP')}`
                      : ''}
                  </p>
                  <button
                    type="button"
                    onClick={() => setDetailIndex(idx)}
                    className="mt-0.5 self-start text-xs text-primary-600 hover:underline"
                  >
                    個別入力を開く →
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </Card>

      {/* 5. 投稿作成 */}
      <Card padding="md" className="mt-4">
        <h2 className="text-sm font-bold text-slate-700 mb-2">5. 投稿を作成</h2>
        {items.length === 0 ? (
          <p className="text-sm text-slate-400">写真を取り込むと、ここから投稿を作成できます。</p>
        ) : (
          <>
            <div className="text-sm text-slate-600 space-y-0.5">
              <p>
                作成できる写真:{' '}
                <span className="font-semibold text-slate-900">{stats.ready} 枚</span>
              </p>
              {stats.noLoc > 0 && (
                <p className="text-warning-700">
                  位置がない: {stats.noLoc} 枚（個別入力で地図から指定すると作成対象になります）
                </p>
              )}
              {stats.noText > 0 && (
                <p className="text-warning-700">
                  タイトル・コメント未入力: {stats.noText}{' '}
                  枚（どちらかを入力すると作成対象になります）
                </p>
              )}
            </div>
            <div className="mt-3 flex flex-wrap items-center gap-2">
              <Button
                variant="primary"
                size="sm"
                disabled={!canCommit}
                onClick={() => {
                  setDryRunInfo(null);
                  setCommitPhase('confirm');
                }}
              >
                投稿を作成（{stats.ready} 枚）
              </Button>
              {needsAuthor && !author && (
                <span className="text-xs text-danger-600">
                  先に投稿者（参加者）を選択してください。
                </span>
              )}
              {stats.readyStreetView > 0 && (
                <span className="text-xs text-slate-500">
                  うち机上調査データ: {stats.readyStreetView} 枚
                </span>
              )}
            </div>
          </>
        )}
      </Card>

      {/* 個別入力モーダル (Phase C) */}
      <Modal
        open={detailItem !== null}
        onClose={() => setDetailIndex(null)}
        closeOnOverlayClick={false}
        size="xl"
        title={
          detailIndex !== null ? `個別入力（${detailIndex + 1} / ${items.length}）` : '個別入力'
        }
      >
        {detailItem && (
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            {/* 左: 写真 + 位置 */}
            <div className="space-y-3">
              <div className="aspect-video bg-slate-100 rounded flex items-center justify-center overflow-hidden">
                {detailPanoramaUrl ? (
                  <PanoramaViewer
                    src={detailPanoramaUrl}
                    baseHeadingDeg={detailItem.photo?.poseHeadingDeg ?? 0}
                    className="w-full h-full"
                    enableFullscreen={false}
                    alt="360度写真のプレビュー"
                  />
                ) : detailItem.thumbUrl ? (
                  <img
                    src={detailItem.thumbUrl}
                    alt=""
                    className="max-w-full max-h-full object-contain"
                  />
                ) : (
                  <span className="text-xs text-slate-400">プレビュー不可（HEIC 等）</span>
                )}
              </div>
              {isPanoramaItem(detailItem) && (
                <label className="block text-sm">
                  <span className="text-slate-600">この360度写真の登録先</span>
                  <select
                    value={detailItem.destination}
                    onChange={(e) =>
                      patchItem(detailItem.id, {
                        destination: e.target.value as BulkPhotoDestination,
                      })
                    }
                    className="mt-1 block w-full rounded border border-slate-300 px-2 py-1.5 text-sm"
                  >
                    {(Object.keys(DESTINATION_LABELS) as BulkPhotoDestination[]).map((dest) => (
                      <option
                        key={dest}
                        value={dest}
                        disabled={dest !== 'post' && !streetViewAvailable}
                      >
                        {DESTINATION_LABELS[dest]}
                      </option>
                    ))}
                  </select>
                  {detailItem.destination !== 'post' && !detailItem.location && (
                    <span className="mt-0.5 block text-[11px] text-warning-700">
                      机上調査データには位置が必要です。地図で指定してください。
                    </span>
                  )}
                  {detailItem.photo?.poseHeadingDeg === null && (
                    <span className="mt-0.5 block text-[11px] text-slate-400">
                      撮影方位が写真に記録されていないため、北を 0° として扱います。
                    </span>
                  )}
                </label>
              )}
              <p className="text-[11px] text-slate-400 truncate" title={detailItem.fileName}>
                {detailItem.fileName}
                {detailItem.captureTimeMs
                  ? ` ・ 撮影 ${new Date(detailItem.captureTimeMs).toLocaleString('ja-JP')}`
                  : ''}
              </p>
              <div>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm font-medium text-slate-700">位置</span>
                  <span className="text-[11px] text-slate-400">
                    {detailItem.location
                      ? detailItem.locationSource === 'manual'
                        ? '手動で指定'
                        : '写真の位置情報'
                      : '未設定（地図をクリックして指定）'}
                  </span>
                </div>
                <LocationPicker
                  // 写真ごとに地図を作り直す: 前後遷移で前の写真のマーカー/中心が残らず、
                  // 位置なし写真では defaultCenter(直前位置の持ち回り) が初期表示に効く。
                  key={detailItem.id}
                  value={detailItem.location}
                  defaultCenter={detailItem.location ?? fallbackCenter}
                  onChange={(loc) => setItemLocation(detailItem.id, loc)}
                />
                {!detailItem.location && lastLocation && (
                  <button
                    type="button"
                    onClick={() => setItemLocation(detailItem.id, lastLocation)}
                    className="mt-1 text-xs text-primary-600 hover:underline"
                  >
                    直前の写真と同じ位置にする
                  </button>
                )}
              </div>
            </div>

            {/* 右: 入力項目 */}
            <div className="space-y-3">
              <label className="block text-sm">
                <span className="text-slate-600">
                  タイトル{' '}
                  {requiredKeys.includes('title') && <span className="text-danger-600">*</span>}
                </span>
                <input
                  type="text"
                  value={detailItem.title}
                  onChange={(e) => patchItem(detailItem.id, { title: e.target.value })}
                  placeholder="タイトル"
                  className="mt-1 block w-full rounded border border-slate-300 px-2 py-1.5 text-sm"
                />
              </label>
              <label className="block text-sm">
                <span className="text-slate-600">
                  コメント{' '}
                  {(requiredKeys.includes('comment') || requiredKeys.includes('detail')) && (
                    <span className="text-danger-600">*</span>
                  )}
                </span>
                <textarea
                  value={detailItem.comment}
                  onChange={(e) => patchItem(detailItem.id, { comment: e.target.value })}
                  placeholder="コメント"
                  rows={3}
                  className="mt-1 block w-full rounded border border-slate-300 px-2 py-1.5 text-sm"
                />
                <span className="mt-0.5 block text-[11px] text-slate-400">
                  必須項目（入力項目設定で「必須」にした項目）が未入力だと投稿できません。
                </span>
              </label>

              <PhotoPostFieldsForm
                postFields={postFields}
                values={detailItem.extraFields}
                onChange={(key, value) => updateExtraField(detailItem.id, key, value)}
              />

              <label className="block text-sm">
                <span className="text-slate-600">公開範囲</span>
                <select
                  value={detailItem.visibility}
                  onChange={(e) =>
                    patchItem(detailItem.id, { visibility: e.target.value as PostVisibility })
                  }
                  className="mt-1 block w-full rounded border border-slate-300 px-2 py-1.5 text-sm"
                >
                  {(Object.keys(VISIBILITY_LABELS) as PostVisibility[]).map((v) => (
                    <option key={v} value={v}>
                      {VISIBILITY_LABELS[v]}
                    </option>
                  ))}
                </select>
              </label>
            </div>

            {/* フッタ: 前後遷移 */}
            <div className="md:col-span-2 flex items-center justify-between border-t border-slate-200 pt-3">
              <Button
                variant="secondary"
                size="sm"
                disabled={detailIndex === 0}
                onClick={() => setDetailIndex((i) => (i === null ? i : Math.max(0, i - 1)))}
              >
                ← 前へ
              </Button>
              <span className="text-xs text-slate-400">←／→ キーでも移動できます</span>
              <Button
                variant="secondary"
                size="sm"
                disabled={detailIndex !== null && detailIndex >= items.length - 1}
                onClick={() =>
                  setDetailIndex((i) => (i === null ? i : Math.min(items.length - 1, i + 1)))
                }
              >
                次へ →
              </Button>
            </div>
          </div>
        )}
      </Modal>

      {/* 確認モーダル (Phase D) */}
      <Modal
        open={commitPhase === 'confirm'}
        onClose={() => setCommitPhase('idle')}
        title="投稿を作成しますか？"
      >
        <div className="space-y-3 text-sm text-slate-700">
          <p>
            投稿者:{' '}
            <span className="font-medium">
              {author?.nickname ?? (needsAuthor ? '(未選択)' : '(机上調査データのみ・不要)')}
            </span>
            {sessionId ? ` / セッション: ${sessionName(sessionId)}` : ' / セッションなし'}
          </p>
          <p>
            作成する投稿: <span className="font-semibold text-slate-900">{stats.readyPost} 件</span>
            {stats.readyStreetView > 0 && (
              <>
                {' '}
                / 机上調査データ:{' '}
                <span className="font-semibold text-slate-900">{stats.readyStreetView} 件</span>
              </>
            )}
          </p>
          {(stats.noLoc > 0 || stats.noText > 0) && (
            <p className="text-xs text-warning-700">
              位置やタイトル/コメントが未入力の{stats.noLoc + stats.noText}
              枚は今回は作成されません（個別入力で補完できます）。
            </p>
          )}
          <p className="text-xs text-slate-500">
            写真は<strong>地区の写真設定（解像度・サイズ・原本保存）に従って</strong>
            保存します（位置は投稿データに記録されます）。HEIC 形式は取り込めない場合があります。
          </p>
          {dryRunInfo && (
            <p className="text-xs text-slate-600">
              サーバ検証: 有効 {dryRunInfo.valid} 件 / エラー {dryRunInfo.errors} 件
            </p>
          )}
        </div>
        <div className="mt-4 flex flex-wrap justify-end gap-2">
          <Button
            variant="secondary"
            size="sm"
            disabled={dryRunning}
            onClick={() => void runDryRun()}
          >
            {dryRunning ? '検証中…' : '検証（ドライラン）'}
          </Button>
          <Button variant="secondary" size="sm" onClick={() => setCommitPhase('idle')}>
            キャンセル
          </Button>
          <Button variant="primary" size="sm" onClick={() => void runCommit()}>
            投稿を作成
          </Button>
        </div>
      </Modal>

      {/* 進捗モーダル */}
      <Modal
        open={commitPhase === 'uploading' || commitPhase === 'creating'}
        onClose={() => {
          /* 進行中は閉じさせない */
        }}
        closeOnOverlayClick={false}
        title={commitPhase === 'uploading' ? '写真をアップロード中…' : '投稿を作成中…'}
      >
        <div className="space-y-3 text-sm text-slate-700">
          {commitPhase === 'uploading' ? (
            <>
              <p>
                写真をアップロードしています（{uploadProgress.done} / {uploadProgress.total}）。
              </p>
              <div className="h-2 w-full rounded bg-slate-200 overflow-hidden">
                <div
                  className="h-full bg-primary-600 transition-all"
                  style={{
                    width: `${
                      uploadProgress.total > 0
                        ? Math.round((uploadProgress.done / uploadProgress.total) * 100)
                        : 0
                    }%`,
                  }}
                />
              </div>
            </>
          ) : (
            <p>投稿データを作成しています。しばらくお待ちください…</p>
          )}
        </div>
      </Modal>

      {/* 結果モーダル */}
      <Modal
        open={commitPhase === 'done'}
        onClose={clearCommitted}
        title="投稿の作成が完了しました"
        intent={commitResult && commitResult.created === 0 ? 'warning' : 'default'}
      >
        {commitResult && (
          <div className="space-y-2 text-sm text-slate-700">
            <p>
              作成: <span className="font-semibold text-slate-900">{commitResult.created} 件</span>
              {commitResult.streetViewCreated > 0 && (
                <>
                  {' '}
                  / 机上調査データ:{' '}
                  <span className="font-semibold text-slate-900">
                    {commitResult.streetViewCreated} 件
                  </span>
                </>
              )}
            </p>
            {commitResult.skipped > 0 && (
              <p className="text-slate-600">
                重複でスキップ: {commitResult.skipped} 件（同じ撮影時刻の投稿が既にあります）
              </p>
            )}
            {commitResult.uploadFailed.length > 0 && (
              <div className="rounded bg-warning-50 border border-warning-200 px-3 py-2 text-xs text-warning-800">
                <p className="font-medium">
                  アップロードできなかった写真: {commitResult.uploadFailed.length} 件
                </p>
                <ul className="mt-1 list-disc list-inside">
                  {commitResult.uploadFailed.slice(0, 5).map((f, i) => (
                    <li key={i} className="truncate">
                      {f.fileName}: {f.message}
                    </li>
                  ))}
                  {commitResult.uploadFailed.length > 5 && (
                    <li>ほか {commitResult.uploadFailed.length - 5} 件</li>
                  )}
                </ul>
              </div>
            )}
            {commitResult.serverErrors.length > 0 && (
              <div className="rounded bg-danger-50 border border-danger-200 px-3 py-2 text-xs text-danger-800">
                <p className="font-medium">エラー: {commitResult.serverErrors.length} 件</p>
                <ul className="mt-1 list-disc list-inside">
                  {commitResult.serverErrors.slice(0, 5).map((e, i) => (
                    <li key={i}>{e.message}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
        <div className="mt-4 flex justify-end">
          <Button variant="primary" size="sm" onClick={clearCommitted}>
            閉じる
          </Button>
        </div>
      </Modal>
    </div>
  );
}
